import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { raptorJobApiService } from '@/raptorjob/infrastructure/rest/raptorJobApi';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';
import { extractRestApiErrorMessage } from '@/common/application/utils/restApiErrorUtils';
import { useFeatureFlag } from '@/common/presentation/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';
import {
  useUpdateRaptorJob as useUpdateRaptorJobOrval,
  useUpdateRaptorJobStatus as useUpdateRaptorJobStatusOrval,
  invalidateJobTicketsQueries
} from '@/raptorjob/application/adapters/useRaptorJobRest';
import { getGetApiV3RaptorJobsIdQueryKey, getGetApiV3RaptorJobsQueryKey } from '@/libs/api-rest/generated/raptor-jobs/raptor-jobs';
import type { RaptorJobDto, UpdateRaptorJobInput, UpdateRaptorJobStatusInput, JobStatusDto } from '@/libs/api-rest/generated/model';

export const RAPTOR_JOB_QUERY_KEYS = {
  raptorJobs: ['raptorJobs'] as const,
  raptorJob: (id: number) => ['raptorJob', id] as const
};

export const useRaptorJobs = (params: string) => {
  return useQuery({
    queryKey: [...RAPTOR_JOB_QUERY_KEYS.raptorJobs, params],
    queryFn: () => raptorJobApiService.getRaptorJobs(params),
    staleTime: 5 * 60 * 1000
  });
};

export const useRaptorJob = (id: number | null) => {
  return useQuery({
    queryKey: id ? RAPTOR_JOB_QUERY_KEYS.raptorJob(id) : [undefined],
    queryFn: () => raptorJobApiService.getRaptorJob(id || 0),
    enabled: !!id
  });
};

// export const useCreateRaptorJob = (): {
//   mutateAsync: (
//     data: RapidWorksRaptorCommonV3InputRaptorJobCreateRaptorJobInput
//   ) => Promise<RaptorJobDto>;
//   isPending: boolean;
// } => {
//   const queryClient = useQueryClient();
//   const { showSnackbar } = useSnackbar();
//   const isOrvalEnabled = useFeatureFlag(FEATURE_FLAGS.ORVAL_ENABLED);
//   const { createJobAsync, isCreating: isCreatingOrval } = useCreateRaptorJobOrval();

//   const legacyMutation = useMutation({
//     mutationFn: (data: RapidWorksRaptorCommonV3InputRaptorJobCreateRaptorJobInput) => raptorJobApiService.createRaptorJob(data),
//     onSuccess: (created: RaptorJobDto) => {
//       if (created?.id) {
//         queryClient.invalidateQueries({ queryKey: RAPTOR_JOB_QUERY_KEYS.raptorJob(created.id) });
//       }
//       queryClient.invalidateQueries({ queryKey: RAPTOR_JOB_QUERY_KEYS.raptorJobs });
//       showSnackbar('Success', 'Raptor job created successfully', 'success');
//     },
//     onError: (error: Error) => {
//       showSnackbar('Error', `Failed to create raptor job: ${error.message}`, 'error');
//     }
//   });

//   return {
//     mutateAsync: async (
//       data: RapidWorksRaptorCommonV3InputRaptorJobCreateRaptorJobInput
//     ): Promise<RaptorJobDto> => {
//       if (isOrvalEnabled) {
//         const response = await createJobAsync({ data });
//         return response.data as RaptorJobDto;
//       }
//       return await legacyMutation.mutateAsync(data);
//     },
//     isPending: isOrvalEnabled ? isCreatingOrval : legacyMutation.isPending
//   };
// };

export const useUpdateRaptorJob = (): {
  mutateAsync: (payload: { id: number; data: UpdateRaptorJobInput }) => Promise<RaptorJobDto>;
  isPending: boolean;
} => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const isOrvalEnabled = useFeatureFlag(FEATURE_FLAGS.ORVAL_ENABLED);
  const { updateJobAsync, isUpdating: isUpdatingOrval } = useUpdateRaptorJobOrval();

  const legacyMutation = useMutation({
    mutationFn: (payload: { id: number; data: UpdateRaptorJobInput }) => raptorJobApiService.updateRaptorJob(payload.id, payload.data),
    onSuccess: (updated: RaptorJobDto, variables) => {
      const jobId = updated?.id != null ? Number(updated.id) : variables.id;
      if (jobId) {
        queryClient.invalidateQueries({ queryKey: RAPTOR_JOB_QUERY_KEYS.raptorJob(jobId) });
        queryClient.invalidateQueries({ queryKey: getGetApiV3RaptorJobsIdQueryKey(jobId) });
        invalidateJobTicketsQueries(queryClient, jobId);
      }
      queryClient.invalidateQueries({ queryKey: RAPTOR_JOB_QUERY_KEYS.raptorJobs });
      queryClient.invalidateQueries({ queryKey: ['raptorTickets'] });
      showSnackbar('Success', 'Raptor job updated successfully', 'success');
    },
    onError: (error: Error) => {
      const responseData = (error as Error & { responseData?: unknown }).responseData;
      const message = extractRestApiErrorMessage(responseData, `Failed to update raptor job: ${error.message}`);
      showSnackbar('Error', message, 'error');
    }
  });

  return {
    mutateAsync: async (payload: { id: number; data: UpdateRaptorJobInput & { legacy?: boolean } }): Promise<RaptorJobDto> => {
      // Always use legacy API service for ERP jobs (legacy=true) to ensure query parameter is added
      if (payload.data.legacy === true) {
        return await legacyMutation.mutateAsync(payload);
      }
      if (isOrvalEnabled) {
        const response = await updateJobAsync(payload);
        return response.data as RaptorJobDto;
      }
      return await legacyMutation.mutateAsync(payload);
    },
    isPending: isOrvalEnabled ? isUpdatingOrval : legacyMutation.isPending
  };
};

export const useUpdateRaptorJobStatus = (): {
  mutateAsync: (payload: { id: number; data: UpdateRaptorJobStatusInput }) => Promise<JobStatusDto>;
  isPending: boolean;
} => {
  const { updateJobStatusAsync, isUpdating } = useUpdateRaptorJobStatusOrval();

  return {
    mutateAsync: async (payload: { id: number; data: UpdateRaptorJobStatusInput }): Promise<JobStatusDto> => {
      const response = await updateJobStatusAsync(payload);
      return response.data as JobStatusDto;
    },
    isPending: isUpdating
  };
};

export const useCancelRaptorJob = (): {
  mutateAsync: (id: number) => Promise<RaptorJobDto>;
  isPending: boolean;
} => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const cancelMutation = useMutation({
    mutationFn: (id: number) => raptorJobApiService.cancelRaptorJob(id, true),
    onSuccess: (updated: RaptorJobDto) => {
      if (updated?.id) {
        queryClient.invalidateQueries({ queryKey: RAPTOR_JOB_QUERY_KEYS.raptorJob(Number(updated.id)) });
        queryClient.invalidateQueries({ queryKey: getGetApiV3RaptorJobsIdQueryKey(Number(updated.id)) });
      }
      // Invalidate all RaptorJobs list queries (including dashboard)
      queryClient.invalidateQueries({ queryKey: RAPTOR_JOB_QUERY_KEYS.raptorJobs });
      queryClient.invalidateQueries({ queryKey: getGetApiV3RaptorJobsQueryKey() });
      queryClient.invalidateQueries({ queryKey: ['raptorTickets'] });
      showSnackbar('Success', 'Raptor job cancelled successfully', 'success');
    },
    onError: (error: Error) => {
      showSnackbar('Error', error.message, 'error');
    }
  });

  return {
    mutateAsync: cancelMutation.mutateAsync,
    isPending: cancelMutation.isPending
  };
};
